import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { GFFrame } from "@/components/GFFrame";
import { getPersonalQuestions, submitPersonalAssessment } from "@/lib/gfApi";

const EMERALD = "#064E3B";
const GOLD = "#D4AF37";

export default function PersonalAssessment() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("gf_personal_user")) {
      navigate("/personal/mula");
      return;
    }
    getPersonalQuestions().then(setData).catch(() => toast.error("Gagal memuatkan soalan."));
  }, [navigate]);

  if (!data) {
    return (
      <GFFrame testId="pg-assessment-loading">
        <div className="flex min-h-screen items-center justify-center text-slate-400">Memuatkan soalan…</div>
      </GFFrame>
    );
  }

  const total = data.total;
  const q = data.questions[idx];
  const current = answers[q.id];
  const progress = ((idx + (current ? 1 : 0)) / total) * 100;
  const factorLabel = data.factor_labels[q.factor];

  const choose = (value) => {
    const next = { ...answers, [q.id]: value };
    setAnswers(next);
    setTimeout(() => {
      if (idx < total - 1) setIdx(idx + 1);
      else finish(next);
    }, 220);
  };

  const finish = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const ordered = data.questions.map((qq) => finalAnswers[qq.id]);
      const user = JSON.parse(sessionStorage.getItem("gf_personal_user"));
      const res = await submitPersonalAssessment({ user, answers: ordered });
      navigate(`/personal/report/${res.id}`);
    } catch (e) {
      setSubmitting(false);
      toast.error("Gagal menghantar jawapan. Sila cuba lagi.");
    }
  };

  return (
    <GFFrame testId="pg-assessment-page">
      <div className="flex min-h-screen flex-col">
        <div className="px-6 pt-8">
          <div className="mb-3 flex items-center justify-between">
            <button onClick={() => (idx > 0 ? setIdx(idx - 1) : navigate("/personal/mula"))}
              data-testid="pg-back-btn" className="flex items-center gap-1 text-sm text-slate-400">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <span className="text-xs font-semibold uppercase tracking-widest" style={{ color: "#047857" }}>{factorLabel}</span>
            <span className="text-sm font-bold text-slate-500">{idx + 1}/{total}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
            <motion.div className="h-full rounded-full" style={{ background: GOLD }}
              animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
          </div>
          <p className="mt-3 text-xs text-slate-400">Soalan {idx + 1} daripada {total}</p>
        </div>

        <div className="flex flex-1 flex-col px-6 py-6">
          <AnimatePresence mode="wait">
            <motion.div key={q.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }} transition={{ duration: 0.3 }} className="flex flex-1 flex-col">
              <div className="flex flex-1 items-center">
                <h2 className="font-heading text-2xl font-semibold leading-snug" style={{ color: EMERALD }}>{q.text}</h2>
              </div>
              <div className="space-y-2.5 pb-6">
                {data.scale.map((s) => {
                  const active = current === s.value;
                  return (
                    <button key={s.value} data-testid={`pg-scale-option-${s.value}`} onClick={() => choose(s.value)}
                      className="flex w-full items-center gap-4 rounded-2xl border px-4 py-3.5 text-left transition-all duration-150 active:scale-[0.99]"
                      style={active
                        ? { background: EMERALD, borderColor: EMERALD, color: "#fff" }
                        : { background: "#fff", borderColor: "#DCE7E1", color: EMERALD }}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold"
                        style={active ? { background: GOLD, color: EMERALD } : { background: "#EAF2ED", color: "#047857" }}>
                        {s.value}
                      </span>
                      <span className="text-[15px] font-medium">{s.label}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {submitting && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/85 backdrop-blur-sm">
            <div className="text-center">
              <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-200" style={{ borderTopColor: GOLD }} />
              <p className="mt-4 text-sm font-semibold text-slate-500">Menyediakan laporan anda…</p>
            </div>
          </div>
        )}
      </div>
    </GFFrame>
  );
}
